import type {
  TermPlan,
  TermCalculation,
  GroupCost,
  PaxCost,
  ExpandedPerson,
  PassengerTicket,
} from './columbusTypes';
import type {
  ColumbusPlannerTermModel,
  ColumbusPlannerPassengerModel,
  ColumbusPlannerTicketModel,
} from '@/api/generated/schemas';

const EMPTY_PLAN: TermPlan = {
  depTicketPrice: null,
  retTicketPrice: null,
  groupCosts: [],
  paxCosts: [],
  tariffs: [],
};

export function emptyPlan(
  groupCosts: GroupCost[],
  paxCosts: PaxCost[],
  tariffCount: number
): TermPlan {
  return {
    ...EMPTY_PLAN,
    groupCosts: groupCosts.map((gc) => ({ name: gc.name, price: null })),
    paxCosts: paxCosts.map((pc) => ({ name: pc.name, price: null })),
    tariffs: Array.from({ length: tariffCount }, () => ({ pax: null, retailPrice: null })),
  };
}

function round(x: number, coef: number) {
  return Math.round(coef * x) / coef;
}

function createUnassignedTickets(
  tickets: ColumbusPlannerTicketModel[],
  dep: boolean
): PassengerTicket[] {
  const result: PassengerTicket[] = [];
  for (let t = tickets.length - 1; t >= 0; t--) {
    const ticket = tickets[t]!;
    if (ticket.isDeparture === dep && ticket.count > ticket.assigned) {
      const pt: PassengerTicket = {
        allocationId: ticket.allocationId,
        cost: ticket.cost,
        code: ticket.code,
        isUnassigned: true,
      };
      for (let p = ticket.assigned; p < ticket.count; p++) result.push(pt);
    }
  }
  return result;
}

export function* expandTermPersons(
  term: ColumbusPlannerTermModel,
  plan: TermPlan
): Generator<ExpandedPerson, void> {
  const tariffs = plan.tariffs ?? [];
  const totalPax = tariffs.reduce((cap, t) => cap + (t.pax ?? 0), 0);
  if (totalPax === 0) return;

  const passengers = term.passengers ?? [];
  const tickets = term.tickets ?? [];

  const groupCosts = plan.groupCosts.reduce<number>((sum, c) => sum + (c.price ?? 0), 0);
  const totalPaxCosts = plan.paxCosts.reduce<number>((sum, c) => sum + (c.price ?? 0), 0);
  const soldRetail = passengers.reduce((sum, p) => sum + p.soldPrice, 0);
  const currentPaxGroupCost =
    passengers.length === 0 ? 0 : Math.round(groupCosts / passengers.length);

  let totalTicketCost = tickets.reduce((sum, t) => sum + t.count * t.cost, 0);
  let totalRetail = 0;
  let pax = 1;

  const unassignedDepTickets = createUnassignedTickets(tickets, true);
  const unassignedRetTickets = createUnassignedTickets(tickets, false);

  for (let t = 0; t < tariffs.length; t++) {
    const tariff = tariffs[t]!;
    const tariffPax = tariff.pax ?? 0;
    for (let p = 0; p < tariffPax; p++) {
      const passenger: ColumbusPlannerPassengerModel | undefined = passengers[pax - 1];
      const retailPrice = passenger?.soldPrice ?? tariff.retailPrice ?? 0;

      const depTicketModel =
        passenger?.depTicketAllocationId != null
          ? tickets.find((tk) => tk.allocationId === passenger.depTicketAllocationId)
          : undefined;
      const depTicket: PassengerTicket | null =
        passenger?.depTicketAllocationId != null
          ? {
              allocationId: passenger.depTicketAllocationId,
              cost: depTicketModel?.cost ?? 0,
              code: depTicketModel?.code ?? null,
            }
          : (unassignedDepTickets.pop() ?? null);

      const retTicketModel =
        passenger?.retTicketAllocationId != null
          ? tickets.find((tk) => tk.allocationId === passenger.retTicketAllocationId)
          : undefined;
      const retTicket: PassengerTicket | null =
        passenger?.retTicketAllocationId != null
          ? {
              allocationId: passenger.retTicketAllocationId,
              cost: retTicketModel?.cost ?? 0,
              code: retTicketModel?.code ?? null,
            }
          : (unassignedRetTickets.pop() ?? null);

      if (!depTicket) totalTicketCost += plan.depTicketPrice ?? 0;
      if (!retTicket) totalTicketCost += plan.retTicketPrice ?? 0;

      const personTicketCost = round(
        (depTicket?.cost ?? plan.depTicketPrice ?? 0) +
          (retTicket?.cost ?? plan.retTicketPrice ?? 0),
        100
      );

      const paxGroupCost = Math.round(groupCosts / pax);
      totalRetail += retailPrice;

      let minBreakingRetail: number | undefined;
      if (!passenger) {
        const missingMarkup = totalTicketCost + groupCosts + totalPaxCosts * pax - soldRetail;
        minBreakingRetail = Math.max(0, Math.ceil(missingMarkup / (pax - passengers.length)));
      }

      yield {
        bcPersonId: passenger?.bcPersonId ?? null,
        businessCaseNumber: passenger?.businessCaseNumber ?? null,
        tariff: t,
        retail: retailPrice,
        depTicket,
        retTicket,
        expense: Math.round(
          passenger
            ? personTicketCost + currentPaxGroupCost + totalPaxCosts
            : personTicketCost + paxGroupCost + totalPaxCosts
        ),
        totalExpense: Math.round(totalTicketCost + groupCosts + totalPaxCosts * pax),
        totalIncome: Math.round(totalRetail),
        minBreakingRetail,
      };

      pax++;
    }
  }
}

export function recomputeTerm(term: ColumbusPlannerTermModel, plan: TermPlan): TermCalculation {
  const passengers = term.passengers ?? [];
  const totalGroupCosts = Math.round(
    plan.groupCosts.reduce<number>((sum, c) => sum + (c.price ?? 0), 0)
  );
  const totalPaxCosts = Math.round(
    plan.paxCosts.reduce<number>((sum, c) => sum + (c.price ?? 0), 0)
  );

  const currentPaxGroupCost =
    passengers.length > 0 && totalGroupCosts
      ? Math.round(totalGroupCosts / passengers.length)
      : undefined;

  let totalCapacity = 0;
  let currentProfit: number | undefined;
  let maxProfit = 0;
  let breakpoint: number | undefined;

  const expander = expandTermPersons(term, plan);
  let item = expander.next();
  while (!item.done) {
    totalCapacity++;
    const profit = item.value.totalIncome - item.value.totalExpense;
    if (totalCapacity === passengers.length) currentProfit = Math.round(profit);
    if (breakpoint === undefined && profit > 0) breakpoint = totalCapacity;
    maxProfit = Math.round(profit);
    item = expander.next();
  }

  // Simplified profit[] array (per tariff at max capacity) — kept for backwards compat
  const depTicket = plan.depTicketPrice ?? 0;
  const retTicket = plan.retTicketPrice ?? 0;
  const perPersonGroupCost = totalCapacity > 0 ? totalGroupCosts / totalCapacity : 0;
  const expense = perPersonGroupCost + totalPaxCosts + depTicket + retTicket;
  const profit = plan.tariffs.map((t) => {
    const retail = t.retailPrice ?? 0;
    return retail - expense;
  });

  return {
    termId: term.id,
    totalPax:
      totalCapacity > 0 ? totalCapacity : plan.tariffs.reduce((s, t) => s + (t.pax ?? 0), 0),
    totalCapacity,
    totalGroupCosts,
    totalPaxCosts,
    expense,
    currentProfit,
    maxProfit,
    breakpoint,
    currentPaxGroupCost,
    profit,
  };
}
